import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import styles from "./AgendaItemDragDrop.module.css";
import { useTranslation } from "react-i18next";
import { AgendaItem, AgendaItemChild } from "src/types";
import { ColorStyle } from "src/utils/Colors";
import { AgendaItemDetails } from "src/screens/assemblies/assembly/agenda/details/AgendaItemDetails";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { LOADING_TYPE } from "src/stores/assemblies/AssemblyStore";

const AgendaItemDragDropBase = observer(() => {
    const { t } = useTranslation();
    const { agendaitemStore, uiStore, assemblyStore } = useContext(RootStoreContext);
    const [openItemId, setOpenItemId] = useState<string | null>(null);

    useEffect(() => {
        if (assemblyStore.loadingType === LOADING_TYPE.ASSEMBLY) {
            agendaitemStore.loadAgendaItems();
        }
    }, [agendaitemStore.currentAgendaItems, assemblyStore.loadingType, agendaitemStore]);

    // Toggle an expanded item
    const toggleItem = useCallback(
        (itemId: string) => {
            setOpenItemId((prevId) => (prevId === itemId ? null : itemId));

            // Find the corresponding item (parent or child) and set it in the store
            const allItems = agendaitemStore.currentAgendaItems;
            let foundItem = allItems.find((item) => item.id === itemId);

            if (!foundItem) {
                // Search in children if not found in parents
                for (const parent of allItems) {
                    foundItem = parent.agendaitemchild?.find((child) => child.id === itemId);
                    if (foundItem) break; // Exit loop if found
                }
            }

            if (foundItem) {
                agendaitemStore.setCurrentAgendaItem(foundItem);
                assemblyStore.setFocusedAgendaItem(foundItem);
            } else {
                console.error(`Item with id ${itemId} not found`);
            }
        },
        [agendaitemStore, assemblyStore]
    );

    const handleDragEnd = useCallback(
        (result: any) => {
            const { source, destination } = result;
            if (!destination) return;

            agendaitemStore.handleDragEnd(source, destination);
        },
        [agendaitemStore]
    );

    const handleAddNewItem = useCallback(
        (isParentAction: boolean, parentIndex: number | null = null, childIndex: number | null = null) =>
            () => {
                agendaitemStore.handleAddNewItem(isParentAction, parentIndex, childIndex);
            },
        [agendaitemStore]
    );

    const handleToggleItem = useCallback(
        (itemId: string) => () => {
            toggleItem(itemId);
        },
        [toggleItem]
    );

    const onClickRemoveAgenda = useCallback(
        (itemId: string) => () => {
            agendaitemStore.removeItemById(itemId);
        },
        [agendaitemStore]
    );

    const addAgendaItem = (isParent: boolean, index: number, parentIndex?: number) => {
        return (
            <>
                <div
                    className={styles.HoverText}
                    onClick={handleAddNewItem(true, isParent ? index : parentIndex)} // Add Parent Item ("Traktandum")
                >
                    <UI.Icon icon={UI.SVGIcon.Add} size={14} className="mr-2" />
                    {t("screens.assemblies.agenda.agendaitem")}
                </div>
                <div
                    className={styles.HoverText}
                    onClick={handleAddNewItem(false, isParent ? index : parentIndex, isParent ? null : index)} // Add Child Item ("Untertraktandum")
                >
                    <UI.Icon icon={UI.SVGIcon.Add} size={14} className="mr-2" />
                    {t("screens.assemblies.agenda.agendaitemchild")}
                </div>
            </>
        );
    };

    const onClickUnlockAgendaItems = useCallback(() => {
        agendaitemStore.setIsModalUnlockDisplayed(true);
    }, [agendaitemStore]);

    // Reusable function to render Parent and Child Items
    const renderItem = (
        item: AgendaItem | AgendaItemChild,
        index: number,
        type: "PARENT" | "CHILD",
        parentIndex?: number | null
    ) => {
        const isParent = type === "PARENT";
        const expanded = openItemId === item.id;
        const isDragDisabled = assemblyStore.isAssemblyReadOnly; // Disable dragging if locked
        const isScreenSizeSmallerMobileL = uiStore.currentScreenSize < SCREEN_SIZE.MOBILE_L;

        return (
            <Draggable draggableId={item.id} index={index} key={item.id} isDragDisabled={isDragDisabled}>
                {(provided) => (
                    <>
                        <div
                            ref={provided.innerRef}
                            {...(!isDragDisabled ? provided.draggableProps : {})} // Prevent drag interaction
                            className={`${isParent ? styles.ParentItem : styles.ChildItem} ${
                                expanded ? styles.ExpandedItem : ""
                            }`}
                        >
                            <div
                                {...(!isDragDisabled ? provided.dragHandleProps : {})} // Disable drag handle if locked
                                className={isDragDisabled ? styles.DisableDragHandle : styles.DragHandle}
                            >
                                {isDragDisabled ? (
                                    <UI.Icon
                                        className={styles.IconLock}
                                        icon={UI.SVGIcon.Lock}
                                        size={"small"}
                                        onClick={onClickUnlockAgendaItems}
                                    />
                                ) : (
                                    <UI.Icon
                                        className={styles.IconFrame}
                                        icon={UI.SVGIcon.Frame}
                                        color={ColorStyle("light-gray")}
                                    />
                                )}
                            </div>
                            <div className={isParent ? styles.ParentRow : styles.ChildRow}>
                                <div className={styles.ItemSort} onClick={handleToggleItem(item.id)}>
                                    {isParent ? `${item.sort}.` : `${parentIndex! + 1}.${item.sort}`}
                                </div>
                                <div
                                    className={isParent ? styles.ItemText : styles.ItemTextChild}
                                    onClick={handleToggleItem(item.id)}
                                >
                                    {item.text || ""}
                                </div>
                                {uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP && !isDragDisabled ? (
                                    <div className={styles.HoverContainer}>
                                        {addAgendaItem(isParent, index, parentIndex!)}
                                        <UI.Icon
                                            icon={UI.SVGIcon.Close}
                                            className={styles.CloseIcon}
                                            size={20}
                                            onClick={onClickRemoveAgenda(item.id)}
                                        />
                                    </div>
                                ) : undefined}

                                {item.agendaitemfiles && item.agendaitemfiles.length > 0 ? (
                                    <UI.Icon icon={UI.SVGIcon.Documents} className={styles.DocumentIcon} size={26} />
                                ) : null}
                                {item.hasVotings ? (
                                    <UI.Icon icon={UI.SVGIcon.Voting} className={styles.VotingIcon} size={32} />
                                ) : null}
                                <UI.Icon
                                    icon={UI.SVGIcon.ChevronDownThin}
                                    className={`${styles.ChevronIcon} ${
                                        expanded ? styles.ChevronOpen : styles.ChevronClosed
                                    }`}
                                    onClick={handleToggleItem(item.id)}
                                />
                            </div>
                        </div>
                        {expanded && (
                            <>
                                <AgendaItemDetails />
                                {assemblyStore.isAssemblyReadOnly ? undefined : (
                                    <div
                                        className={`${styles.AddAgendaItems} ${
                                            isScreenSizeSmallerMobileL ? styles.AddAgendaItemsMobileSmall : ""
                                        }`}
                                    >
                                        {addAgendaItem(isParent, index, parentIndex!)}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </Draggable>
        );
    };

    return (
        <UI.Container className={styles.AgendaItemContainer}>
            <DragDropContext onDragEnd={assemblyStore.isAssemblyReadOnly ? undefined : handleDragEnd}>
                <Droppable droppableId="agenda-list" type="PARENT">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {agendaitemStore.currentAgendaItems.map((parentItem, parentIndex) => (
                                <React.Fragment key={parentItem.id}>
                                    {renderItem(parentItem, parentIndex, "PARENT")}

                                    <Droppable droppableId={`children-${parentItem.id}`} type="CHILD">
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                                {parentItem.agendaitemchild?.map((childItem, childIndex) =>
                                                    renderItem(childItem, childIndex, "CHILD", parentIndex)
                                                )}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </React.Fragment>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </UI.Container>
    );
});

export const AgendaItemDragDrop = Sentry.withProfiler(AgendaItemDragDropBase);
